import { type DateFormat, type Timezone } from "@/types/settings";

export type DateInput = Date | string | number | null | undefined;

export interface DateFormatOptions {
  /** Raw `preferences.date_format` value; unrecognized values fall back to {@link DEFAULT_DATE_FORMAT}. */
  dateFormat?: string;
  /** Raw `preferences.timezone` value; invalid IANA ids fall back to {@link DEFAULT_TIMEZONE}. */
  timezone?: string;
}

export const DEFAULT_DATE_FORMAT: DateFormat = "DD/MM/YYYY";
export const DEFAULT_TIMEZONE: Timezone = "Europe/Copenhagen";
export const EMPTY_DATE_PLACEHOLDER = "—";

/**
 * The stored `preferences.timezone` the old backend default wrote (#952).
 * Rows created before #952 hold the literal "UTC" whether or not the user ever
 * chose it, so it is read as "not set", exactly like the backend's
 * `resolve_display_timezone`. An explicit UTC choice is stored as "Etc/UTC".
 */
export const LEGACY_DEFAULT_TIMEZONE = "UTC";

/**
 * Turns a raw `preferences.timezone` value into the timezone to render in:
 * missing, non-string, blank and legacy-default ("UTC") values resolve to
 * {@link DEFAULT_TIMEZONE}. Anything else is passed through; the formatters
 * fall back to the default for invalid IANA ids.
 */
export function resolveTimezonePreference(raw: unknown): string {
  if (typeof raw !== "string" || raw.trim() === "" || raw === LEGACY_DEFAULT_TIMEZONE) {
    return DEFAULT_TIMEZONE;
  }
  return raw;
}

const MONTH_ONLY_PATTERN = /^\d{4}-\d{2}$/;
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

interface DateParts {
  dd: string;
  mm: string;
  yyyy: string;
  hh: string;
  min: string;
}

const DATE_TEMPLATES: Record<DateFormat, (parts: DateParts) => string> = {
  "DD/MM/YYYY": p => `${p.dd}/${p.mm}/${p.yyyy}`,
  "MM/DD/YYYY": p => `${p.mm}/${p.dd}/${p.yyyy}`,
  "YYYY-MM-DD": p => `${p.yyyy}-${p.mm}-${p.dd}`,
  "DD.MM.YYYY": p => `${p.dd}.${p.mm}.${p.yyyy}`,
};

function resolveDateFormat(dateFormat: string | undefined): DateFormat {
  return dateFormat !== undefined && dateFormat in DATE_TEMPLATES
    ? (dateFormat as DateFormat)
    : DEFAULT_DATE_FORMAT;
}

interface ParsedInput {
  date: Date;
  /**
   * Calendar dates ("2026-07-21", "2026-07") carry no time-of-day, so they are
   * parsed AND rendered in UTC — converting them to the user's timezone would
   * shift them across midnight (e.g. a start date rendering as the day before).
   */
  renderTimezone?: "UTC";
}

function parseInput(value: DateInput): ParsedInput | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : { date: value };
  }
  if (typeof value === "number") {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : { date };
  }
  const trimmed = value.trim();
  if (trimmed === "") {
    return null;
  }
  if (MONTH_ONLY_PATTERN.test(trimmed)) {
    const date = new Date(`${trimmed}-01T00:00:00Z`);
    return isNaN(date.getTime()) ? null : { date, renderTimezone: "UTC" };
  }
  if (DATE_ONLY_PATTERN.test(trimmed)) {
    const date = new Date(`${trimmed}T00:00:00Z`);
    return isNaN(date.getTime()) ? null : { date, renderTimezone: "UTC" };
  }
  const date = new Date(trimmed);
  return isNaN(date.getTime()) ? null : { date };
}

function createFormatter(
  options: Intl.DateTimeFormatOptions,
  timeZone: string
): Intl.DateTimeFormat {
  try {
    return new Intl.DateTimeFormat("en-GB", { ...options, timeZone });
  } catch {
    return new Intl.DateTimeFormat("en-GB", { ...options, timeZone: DEFAULT_TIMEZONE });
  }
}

function getParts(date: Date, timeZone: string, withTime: boolean): DateParts {
  const formatter = createFormatter(
    {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      // hourCycle (not hour12) — some ICU versions render midnight as "24:00" with hour12: false
      ...(withTime
        ? { hour: "2-digit" as const, minute: "2-digit" as const, hourCycle: "h23" as const }
        : {}),
    },
    timeZone
  );
  const parts: DateParts = { dd: "", mm: "", yyyy: "", hh: "", min: "" };
  for (const part of formatter.formatToParts(date)) {
    switch (part.type) {
      case "day":
        parts.dd = part.value;
        break;
      case "month":
        parts.mm = part.value;
        break;
      case "year":
        parts.yyyy = part.value;
        break;
      case "hour":
        parts.hh = part.value;
        break;
      case "minute":
        parts.min = part.value;
        break;
    }
  }
  return parts;
}

/**
 * Formats a date according to the user's `date_format` and `timezone`
 * preferences, e.g. "21/07/2026". Missing or unparseable input renders as
 * {@link EMPTY_DATE_PLACEHOLDER}; this function never throws.
 */
export function formatDate(value: DateInput, options?: DateFormatOptions): string {
  const parsed = parseInput(value);
  if (!parsed) {
    return EMPTY_DATE_PLACEHOLDER;
  }
  const timeZone = parsed.renderTimezone ?? options?.timezone ?? DEFAULT_TIMEZONE;
  const parts = getParts(parsed.date, timeZone, false);
  return DATE_TEMPLATES[resolveDateFormat(options?.dateFormat)](parts);
}

/**
 * Formats a timestamp as {@link formatDate} plus a fixed 24-hour clock time,
 * e.g. "21/07/2026 14:30". There is no `time_format` preference, so the time
 * part is always 24h.
 */
export function formatDateTime(value: DateInput, options?: DateFormatOptions): string {
  const parsed = parseInput(value);
  if (!parsed) {
    return EMPTY_DATE_PLACEHOLDER;
  }
  const timeZone = parsed.renderTimezone ?? options?.timezone ?? DEFAULT_TIMEZONE;
  const parts = getParts(parsed.date, timeZone, true);
  return `${DATE_TEMPLATES[resolveDateFormat(options?.dateFormat)](parts)} ${parts.hh}:${parts.min}`;
}

/**
 * Formats a month + year with a readable month name ("Jul 2026" or
 * "July 2026"), for ranges like experience or company durations where a
 * numeric date would be noise. Accepts "YYYY-MM" strings as calendar months.
 */
export function formatMonthYear(
  value: DateInput,
  options?: DateFormatOptions & { monthStyle?: "short" | "long" }
): string {
  const parsed = parseInput(value);
  if (!parsed) {
    return EMPTY_DATE_PLACEHOLDER;
  }
  const timeZone = parsed.renderTimezone ?? options?.timezone ?? DEFAULT_TIMEZONE;
  const formatter = createFormatter(
    { month: options?.monthStyle ?? "short", year: "numeric" },
    timeZone
  );
  return formatter.format(parsed.date);
}

/**
 * Relative time for activity feeds: "just now", "5 minutes ago", "3 hours
 * ago", "2 days ago"; anything a week or older renders via {@link formatDate}
 * with the user's preferences.
 */
export function formatRelative(value: DateInput, options?: DateFormatOptions): string {
  const parsed = parseInput(value);
  if (!parsed) {
    return EMPTY_DATE_PLACEHOLDER;
  }
  const diffInSeconds = Math.floor((Date.now() - parsed.date.getTime()) / 1000);
  if (diffInSeconds < 60) {
    return "just now";
  }
  if (diffInSeconds < 3600) {
    return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  }
  if (diffInSeconds < 86400) {
    return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  }
  if (diffInSeconds < 604800) {
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  }
  return formatDate(value, options);
}
